import type { ReactNode } from 'react';
import {
  CRITICALITY_LABELS_UK,
  DEVICE_TYPE_LABELS_UK,
  STATUS_COLORS,
  STATUS_LABELS_UK,
  displayPortId,
  listNodePorts,
  type DiagramEdge,
  type DiagramNode,
  type NodeStatus,
} from '@ukrop-nm/shared';
import { normalizeMac, resolveOuiVendor } from '../device/deviceAppearance';

const CONNECTION_TYPE_UK: Record<string, string> = {
  ethernet: 'Ethernet',
  fiber: 'Оптоволокно',
  wifi: 'Wi‑Fi',
  wan: 'WAN',
  sip: 'SIP',
  logical: 'Логічне',
  unknown: 'Невідомо',
};

const MONITOR_SOURCE_UK: Record<string, string> = {
  interface: 'Інтерфейс',
  neighbor: 'Сусід',
  'endpoint-derived': 'Від кінцевої точки',
  manual: 'Вручну',
  unknown: 'Невідомо',
};

const DASH = '—';

function val(v: string | number | null | undefined): string {
  if (v == null) return DASH;
  const t = String(v).trim();
  return t || DASH;
}

function TipRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ukrop-tip-row">
      <span className="ukrop-tip-label">{label}:</span>
      <span className="ukrop-tip-value">{children}</span>
    </div>
  );
}

function StatusLamp({ status }: { status: NodeStatus }) {
  const color = STATUS_COLORS[status];
  return (
    <span className="ukrop-tip-status">
      <i
        className="ukrop-status-lamp"
        style={{ background: color, boxShadow: `0 0 0 2px ${color}33` }}
        aria-hidden
      />
      <span>{STATUS_LABELS_UK[status]}</span>
    </span>
  );
}

function ethLanSummary(node: DiagramNode, occupiedPorts?: string[]): string {
  const ports = listNodePorts(node);
  const eth = ports.filter((p) => p.kind === 'ethernet');
  const total =
    node.interfaces?.hasEthernet && (node.interfaces.ethernetCount ?? 0) > 0
      ? node.interfaces.ethernetCount!
      : eth.length;
  if (total <= 0 && eth.length === 0) return DASH;
  const occupied = new Set(occupiedPorts ?? []);
  const busy = eth.filter((p) => occupied.has(p.id)).length;
  return `${total || eth.length} / ${busy}`;
}

function sfpSummary(node: DiagramNode): string {
  const iface = node.interfaces;
  if (iface?.hasSfp && (iface.sfpCount ?? 0) > 0) return String(iface.sfpCount);
  return DASH;
}

function wifiSummary(node: DiagramNode): string {
  const iface = node.interfaces;
  if (!iface?.hasWifi || !(iface.wifiCount && iface.wifiCount > 0)) return DASH;
  const bits = [String(iface.wifiCount)];
  if (iface.wifi24) bits.push('2.4 ГГц');
  if (iface.wifi58) bits.push('5.8 ГГц');
  return bits.join(' · ');
}

/** Structured device tooltip (fixed field order). */
export function buildNodeTooltipContent(
  node: DiagramNode,
  opts?: { status?: NodeStatus; occupiedPorts?: string[] },
): ReactNode {
  const inv = node.inventory ?? {};
  const mac = normalizeMac(inv.macLocal) ?? inv.macLocal?.trim() ?? '';
  const ouiVendor = resolveOuiVendor(inv.macLocal);
  const vendor = inv.vendor?.trim() || ouiVendor || '';
  const status = opts?.status ?? 'unknown';

  return (
    <div className="ukrop-tip-grid">
      <TipRow label="Тип">{val(DEVICE_TYPE_LABELS_UK[node.type])}</TipRow>
      <TipRow label="Виробник">{val(vendor)}</TipRow>
      <TipRow label="Модель">{val(inv.model)}</TipRow>
      <TipRow label="Назва">{val(node.label)}</TipRow>
      <TipRow label="IP Address">{val(inv.ipLocal)}</TipRow>
      <TipRow label="MAC Address">{val(mac)}</TipRow>
      <TipRow label="Ethernet / LAN">{ethLanSummary(node, opts?.occupiedPorts)}</TipRow>
      <TipRow label="SFP">{sfpSummary(node)}</TipRow>
      <TipRow label="Wi‑Fi">{wifiSummary(node)}</TipRow>
      <TipRow label="Критичність">
        {CRITICALITY_LABELS_UK[node.criticality] ?? node.criticality}
      </TipRow>
      <TipRow label="Стан">
        <StatusLamp status={status} />
      </TipRow>
    </div>
  );
}

/** Structured edge tooltip. */
export function buildEdgeTooltipContent(edge: DiagramEdge): ReactNode {
  const ports =
    edge.sourcePort || edge.targetPort
      ? `${displayPortId(edge.sourcePort)} → ${displayPortId(edge.targetPort)}`
      : DASH;

  return (
    <div className="ukrop-tip-grid">
      <TipRow label="Назва">{val(edge.label)}</TipRow>
      <TipRow label="Тип">{val(CONNECTION_TYPE_UK[edge.connectionType] ?? edge.connectionType)}</TipRow>
      <TipRow label="Мережа">{val(edge.networkCidr)}</TipRow>
      <TipRow label="Порти">{ports}</TipRow>
      <TipRow label="Ключ моніторингу">{val(edge.monitorKey)}</TipRow>
      <TipRow label="Джерело">{val(MONITOR_SOURCE_UK[edge.monitorSource] ?? edge.monitorSource)}</TipRow>
      <TipRow label="Критичність">
        {CRITICALITY_LABELS_UK[edge.criticality] ?? edge.criticality}
      </TipRow>
      <TipRow label="Нотатки">{val(edge.notes)}</TipRow>
    </div>
  );
}

export function buildPortTooltipText(portLabel: string, busy: boolean): string {
  return `${portLabel} · ${busy ? 'зайнятий' : 'вільний'}`;
}
